import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateToken, TOKEN_HEADER } from '../utils/tokenUtils';
import { API_ENDPOINTS } from '../config/constants';
import '../styles/events.css';

// Map Font Awesome icon classes to emoji icons
const getIconFromFontAwesome = (iconClass) => {
  const iconMap = {
    'fa-laptop-code': '💻',
    'fa-briefcase': '💼',
    'fa-graduation-cap': '🎓',
    'fa-users': '👥',
    'fa-running': '🏃',
    'fa-palette': '🎨',
    'fa-heart': '❤️',
    'fa-calendar': '📅',
    'fa-star': '⭐',
    'fa-music': '🎵',
    'fa-film': '🎬',
    'fa-book': '📚',
    'fa-coffee': '☕',
    'fa-utensils': '🍴',
    'fa-camera': '📷',
    'fa-gamepad': '🎮',
    'fa-plane': '✈️',
    'fa-basketball-ball': '🏀',
    'fa-football-ball': '⚽',
    'fa-dumbbell': '🏋️',
  };
  
  return iconMap[iconClass] || '📌';
};

const Events = () => {
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showRSVPModal, setShowRSVPModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [rsvpForm, setRSVPForm] = useState({
    name: '',
    email: '',
    mobile: '',
    status: 'going',
    guests_count: 0
  });
  const [rsvpLoading, setRSVPLoading] = useState(false);
  const [rsvpSuccess, setRSVPSuccess] = useState(false);

  useEffect(() => {
    fetchEvents();
    fetchCategories();
  }, []);

  useEffect(() => {
    filterEvents();
  }, [events, selectedCategory, searchQuery]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/events?status=published&limit=100`, {
        headers: {
          [TOKEN_HEADER]: generateToken()
        }
      });
      const data = await response.json();
      if (data.success) {
        setEvents(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/events/categories/list`, {
        headers: {
          [TOKEN_HEADER]: generateToken()
        }
      });
      const data = await response.json();
      if (data.success) {
        setCategories(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const filterEvents = () => {
    let filtered = [...events];

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(event => event.category_id === parseInt(selectedCategory));
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(event =>
        event.title.toLowerCase().includes(query) ||
        (event.description && event.description.toLowerCase().includes(query)) ||
        (event.location_name && event.location_name.toLowerCase().includes(query))
      );
    }

    // Sort by date (upcoming first)
    filtered.sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime));

    setFilteredEvents(filtered);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };
    return date.toLocaleString('en-US', options);
  };

  const getLocationDisplay = (event) => {
    if (event.location_type === 'virtual') {
      return '💻 Online event';
    } else if (event.location_type === 'physical' && event.location_name) {
      return `📍 ${event.location_name}`;
    }
    return '📍 Location TBA';
  };

  const handleRSVPClick = (event) => {
    setSelectedEvent(event);
    setShowRSVPModal(true);
    setRSVPSuccess(false);
  };

  const handleRSVPSubmit = async (e) => {
    e.preventDefault();
    setRSVPLoading(true);

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/events/${selectedEvent.id}/rsvp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [TOKEN_HEADER]: generateToken()
        },
        body: JSON.stringify(rsvpForm)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setRSVPSuccess(true);
        setRSVPForm({
          name: '',
          email: '',
          mobile: '',
          status: 'going',
          guests_count: 0
        });
        // Refresh events to update counts
        setTimeout(() => {
          fetchEvents();
          setShowRSVPModal(false);
        }, 2000);
      } else {
        alert(data.error || 'Failed to submit RSVP. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting RSVP:', error);
      alert('Network error. Please check your connection and try again.');
    } finally {
      setRSVPLoading(false);
    }
  };

  const closeModal = () => {
    setShowRSVPModal(false);
    setSelectedEvent(null);
    setRSVPSuccess(false);
  };

  const navigate = useNavigate();

  const handleEventClick = (event) => {
    sessionStorage.setItem('selectedEvent', JSON.stringify(event));
    const titleSlug = event.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    const typeSlug = (event.location_type || 'event').toLowerCase();
    const locationSlug = event.location_name ? event.location_name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') : 'tba';
    const slug = event.slug || `${titleSlug}-${typeSlug}-${locationSlug}`;
    navigate(`/events/details?id=${event.id}&slug=${slug}`);
  };

  const upcomingEvents = filteredEvents.filter(e => new Date(e.start_datetime) > new Date());
  const pastEvents = filteredEvents.filter(e => new Date(e.start_datetime) <= new Date());

  return (
    <div className="events-page">
      <div className="events-header">
        <div className="events-header-content">
          <h1>Discover Events</h1>
          <p>Join our community events and grow your skills</p>
          <div className="events-search">
            <input
              type="text"
              placeholder="Search events..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
        </div>
      </div>

      <div className="events-container">
        <aside className="events-sidebar">
          <div className="events-filters">
            <h3>Categories</h3>
            <div className="category-list">
              <button
                className={`category-item ${selectedCategory === 'all' ? 'active' : ''}`}
                onClick={() => setSelectedCategory('all')}
              >
                <span className="category-icon">🎯</span>
                All Events
                <span className="category-count">{events.length}</span>
              </button>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  className={`category-item ${selectedCategory === cat.id ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(cat.id)}
                >
                  <span className="category-icon">{getIconFromFontAwesome(cat.icon)}</span>
                  {cat.name}
                  <span className="category-count">
                    {events.filter(e => e.category_id === cat.id).length}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </aside>

        <main className="events-main">
          {loading ? (
            <div className="events-loading">
              <div className="spinner"></div>
              <p>Loading events...</p>
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="events-empty">
              <div className="empty-icon">📅</div>
              <h2>No events found</h2>
              <p>Try adjusting your filters or search query</p>
            </div>
          ) : (
            <>
              {upcomingEvents.length > 0 && (
                <section className="events-section">
                 
                  <div className="events-grid">
                    {upcomingEvents.map(event => (
                      <div key={event.id} className="event-card" onClick={() => handleEventClick(event)}>
                        <div className="event-image">
                          {event.image_url && (
                            <img src={event.image_url} alt={event.title} />
                          )}
                          <div className="event-title-overlay">{event.title}</div>
                        </div>
                        <div className="event-content">
                          <div className="event-date">
                            {formatDate(event.start_datetime)}
                          </div>
                          <h3 className="event-title">{event.title}</h3>
                          {event.description && (
                            <p className="event-description">
                              {event.description.substring(0, 150)}
                              {event.description.length > 150 ? '...' : ''}
                            </p>
                          )}
                          <div className="event-meta">
                            <span className="event-location">
                              {getLocationDisplay(event)}
                            </span>
                            {event.category_name && (
                              <span className="event-category">
                                {event.category_name}
                              </span>
                            )}
                          </div>
                          <div className="event-footer">
                            <div className="event-attendees">
                              <span className="attendees-icon">👥</span>
                              <span className="attendees-count">
                                {event.going_count || 0} attending
                              </span>
                            </div>
                            <button
                              className="rsvp-button"
                              onClick={(e) => { e.stopPropagation(); handleRSVPClick(event); }}
                            >
                              RSVP Now
                            </button>
                          </div>
                          {event.is_featured && (
                            <div className="featured-badge">⭐ Featured</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {pastEvents.length > 0 && (
                <section className="events-section">
                  <h2 className="section-title">Past Events</h2>
                  <div className="events-grid">
                    {pastEvents.map(event => (
                      <div key={event.id} className="event-card past-event" onClick={() => handleEventClick(event)}>
                        <div className="event-image">
                          {event.image_url && (
                            <img src={event.image_url} alt={event.title} />
                          )}
                          <div className="event-title-overlay">{event.title}</div>
                          <div className="past-overlay">Past Event</div>
                        </div>
                        <div className="event-content">
                          <div className="event-date">
                            {formatDate(event.start_datetime)}
                          </div>
                          <h3 className="event-title">{event.title}</h3>
                          <div className="event-meta">
                            <span className="event-location">
                              {getLocationDisplay(event)}
                            </span>
                          </div>
                          <div className="event-footer">
                            <div className="event-attendees">
                              <span className="attendees-icon">👥</span>
                              <span className="attendees-count">
                                {event.going_count || 0} attended
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </main>
      </div>

      {/* RSVP Modal */}
      {showRSVPModal && selectedEvent && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="rsvp-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>×</button>
            
            {rsvpSuccess ? (
              <div className="rsvp-success">
                <div className="success-icon">✅</div>
                <h2>RSVP Confirmed!</h2>
                <p>Thank you for registering for <strong>{selectedEvent.title}</strong></p>
                <p className="success-message">
                  We've sent a confirmation email to {rsvpForm.email}
                </p>
              </div>
            ) : (
              <>
                <h2>RSVP for {selectedEvent.title}</h2>
                <p className="modal-subtitle">
                  {formatDate(selectedEvent.start_datetime)}
                </p>

                <form onSubmit={handleRSVPSubmit} className="rsvp-form">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      value={rsvpForm.name}
                      onChange={(e) => setRSVPForm({ ...rsvpForm, name: e.target.value })}
                      required
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      value={rsvpForm.email}
                      onChange={(e) => setRSVPForm({ ...rsvpForm, email: e.target.value })}
                      required
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="mobile">Mobile Number (Optional)</label>
                    <input
                      type="tel"
                      id="mobile"
                      value={rsvpForm.mobile}
                      onChange={(e) => setRSVPForm({ ...rsvpForm, mobile: e.target.value })}
                      placeholder="+1 234 567 8900"
                    />
                  </div>

                  <div className="form-group">
                    <label>RSVP Status *</label>
                    <div className="status-options">
                      <label className="status-option">
                        <input
                          type="radio"
                          name="status"
                          value="going"
                          checked={rsvpForm.status === 'going'}
                          onChange={(e) => setRSVPForm({ ...rsvpForm, status: e.target.value })}
                        />
                        <span className="status-label">
                          <span className="status-icon">✓</span>
                          Going
                        </span>
                      </label>
                      <label className="status-option">
                        <input
                          type="radio"
                          name="status"
                          value="interested"
                          checked={rsvpForm.status === 'interested'}
                          onChange={(e) => setRSVPForm({ ...rsvpForm, status: e.target.value })}
                        />
                        <span className="status-label">
                          <span className="status-icon">⭐</span>
                          Interested
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="guests">Number of Guests</label>
                    <select
                      id="guests"
                      value={rsvpForm.guests_count}
                      onChange={(e) => setRSVPForm({ ...rsvpForm, guests_count: parseInt(e.target.value) })}
                    >
                      <option value="0">Just me</option>
                      <option value="1">+1 guest</option>
                      <option value="2">+2 guests</option>
                      <option value="3">+3 guests</option>
                      <option value="4">+4 guests</option>
                      <option value="5">+5 guests</option>
                    </select>
                  </div>

                  <button
                    type="submit"
                    className="submit-button"
                    disabled={rsvpLoading}
                  >
                    {rsvpLoading ? 'Submitting...' : 'Confirm RSVP'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Events;
